import React from 'react';
import styled from 'styled-components';
import { IconButton, Spinner, Text, Stack } from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { useTOC } from './useTOC';
import { usePageContext } from '../../hooks/PageContext';

// ✅ Контейнер оглавления: в потоке флекса, толкает мэйн
const TOCContainer = styled.aside<{ isopen: boolean; ismobile: boolean; sidebaropen: boolean }>`
  width: ${({ isopen }) => (isopen ? '280px' : '0px')};
  margin-right: ${({ isopen, ismobile }) => (!ismobile && isopen ? '30px' : '0')};
  background-color: #fff;
  transition: width 0.3s ease, margin-right 0.3s ease;
  overflow: hidden;
  flex-shrink: 0;
  height: 100dvh;
  border-right: ${({ isopen }) => (isopen ? '1px solid #edebe9' : 'none')};

  ${({ ismobile, sidebaropen }) =>
    ismobile &&
    `
    position: fixed;
    top: 0;
    bottom: 0;
    left: ${sidebaropen ? '290px' : '45px'};
    z-index: 1090;
    margin-right: 0;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.2);
  `}
`;

interface TOCSidebarProps {
  isOpen: boolean;
  isMobile: boolean;
  sidebarOpen: boolean;
  onClose: () => void;
}

export const TOCSidebar: React.FC<TOCSidebarProps> = ({ isOpen, isMobile, sidebarOpen, onClose }) => {
  const { categories, loading } = useTOC();
  const navigate = useNavigate();
  const { actions } = usePageContext();
  const { setMode } = actions;

  return (
    <TOCContainer isopen={isOpen} ismobile={isMobile} sidebaropen={sidebarOpen}>
      {/* ✅ Внутренний контейнер фиксированной ширины, чтобы контент не "схлопывался" при анимации */}
      <div style={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 8px 8px 16px' }}>
          <Text variant="large" styles={{ root: { fontWeight: 600 } }}>Оглавление</Text>
          <IconButton
            iconProps={{ iconName: 'ChevronLeft' }}
            title="Свернуть оглавление"
            onClick={onClose}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 16px 16px' }}>
          {loading ? (
            <Spinner label="Загрузка..." />
          ) : (
            <Stack tokens={{ childrenGap: 14 }}>
              {categories.map((cat) => (
                <div key={cat.id}>
                <Text
                  variant="mediumPlus"
                  styles={{ 
                    root: { 
                      fontWeight: 600, 
                      color: '#0078d4', 
                      display: 'block', 
                      marginBottom: 6,
                      cursor: 'pointer',
                    } 
                  }}
                  onClick={() => {
                    // ✅ Переход на блог с фильтром по тегу через URL
                    navigate(`/blog?tag=${encodeURIComponent(cat.name)}`);
                    if (isMobile) onClose();
                  }}
                >
                  {cat.name}
                </Text>
                  <Stack tokens={{ childrenGap: 2 }}>
                    {cat.articles.map((article) => (
                      <div
                        key={article.id}
                        role="link"
                        tabIndex={0}
                        onClick={() => {
                          setMode('list'); 
                          navigate(`/blog/${article.id}`);
                          if (isMobile) onClose();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') navigate(`/blog/${article.id}`);
                        }}
                        style={{
                          padding: '6px 8px',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 13,
                          color: '#323130',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f2f1')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        {article.title}
                      </div>
                    ))}
                  </Stack>
                </div>
              ))}
            </Stack>
          )}
        </div>
      </div>
    </TOCContainer>
  );
};